import { api } from './api'
import type {
    Bill,
    BillsResponse,
    ListBillsParams,
    ProcessBillActionRequest
} from '@/types/bill'
import type { CreateJobResponse } from '@/types/job'
import { useMutation } from '@tanstack/react-query'

const transformBillResponse = (data: any): Bill => {
    
    // Get top-level artifact keys
    const artifactKey = data.LegislativeReviewS3Key || data.legislative_review_s3_key;
    
    // Prepare versions array, checking both snake_case and PascalCase
    const versions = Array.isArray(data.Versions || data.versions) 
        ? (data.Versions || data.versions)
        : [];
    
    // If no versions but top-level key exists, create a dummy version so dropdown still renders
    const billVersions = versions.length > 0
        ? versions
        : (artifactKey ? [{
            Version: 1,
            MD5Hash: data.MD5Hash || data.md5_hash,
            CreatedAt: data.LastInteractionTime || data.last_interaction_time || new Date().toISOString(),
            TextExtracted: !!data.TextS3Key || !!data.text_s3_key,
            LegislativeReviewS3Key: artifactKey
        }] : []);
    
    // Extract impact score from monitor analysis
    const monitorAnalysis = data.MonitorAnalysis || data.monitor_analysis;
    const impactScore = monitorAnalysis?.summary?.overall_impact?.score || 0;
    
    // Handle both snake_case (API) and PascalCase (legacy) formats
    return {
        id: data.SK || data.bill_number,
        billNumber: data.SK || data.bill_number,
        title: data.Title || data.title,
        synopsis: data.Synopsis || data.synopsis,
        status: data.SecondReadingActionHouseOfOrigin || 'Unknown',
        chamber: data.Chamber,
        crossfileBillNumber: data.CrossfileBillNumber || data.crossfile_bill_number,
        session: data.Session || data.session,
        lastModified: data.LastProcessedTime || data.last_interaction_time || new Date().toISOString(),
        url: data.PDFURL || data.pdf_url,
        fullTextAvailable: data.FullTextAvailable || data.full_text_available,
        monitor_analysis: data.MonitorAnalysis || data.monitor_analysis,
        impactScore: impactScore,
        versions: billVersions.map((v: any) => ({
            version: v.Version || v.version,
            md5Hash: v.MD5Hash || v.md5_hash,
            createdAt: v.CreatedAt || v.created_at,
            textExtracted: v.TextExtracted || v.text_extracted,
            textractJobId: v.TextractJobId || v.textract_job_id,
            textS3Key: v.TextS3Key || v.text_s3_key,
            legislativeReviewS3Key: v.LegislativeReviewS3Key || v.legislative_review_s3_key || artifactKey,
            refinementS3Key: v.RefinementS3Key || v.refinement_s3_key,
            oppositionS3Key: v.OppositionS3Key || v.opposition_s3_key,
            supportS3Key: v.SupportS3Key || v.support_s3_key,
            shouldMonitorS3Key: v.ShouldMonitorS3Key || v.should_monitor_s3_key
        }))
    };
}

export const billsApi = {
    getBill: async (sessionCode: string, billNumber: string): Promise<Bill> => {
        try {
            const encodedSession = encodeURIComponent(sessionCode)
            const response = await api.get<any>(`/bills/${encodedSession}/${billNumber}`, {
                isWorkspaceSpecific: true
            })
            return transformBillResponse(response.data)
        } catch (error) {
            console.error('Failed to fetch bill:', error)
            throw error
        }
    },

    getPresignedUrl: async (s3Key: string): Promise<string> => {
        try {
            const response = await api.get<{ url: string }>(`/bills/files/${s3Key}`, {
                isWorkspaceSpecific: true
            })
            return response.data.url
        } catch (error) {
            console.error('Failed to get presigned URL:', error)
            throw error
        }
    },

    listBills: async ({nextToken, minImpactScore}: ListBillsParams): Promise<BillsResponse> => {
        const params = new URLSearchParams()
        if (nextToken) {
            const tokenStr = typeof nextToken === 'object' ? JSON.stringify(nextToken) : nextToken
            params.append('next_token', tokenStr)
        }
        
        // Add impact score filtering
        if (minImpactScore !== undefined) {
            params.append('min_impact_score', minImpactScore.toString())
            console.log(`[billsApi] Filtering by min_impact_score=${minImpactScore}`)
        }

        try {
            const requestUrl = `/bills?${params.toString()}`
            console.log(`[billsApi] Making request to: ${requestUrl}`)
            
            // Make the API request
            const response = await api.get<any>(requestUrl, {
                isWorkspaceSpecific: true
            })
            console.log(`[billsApi] Response status: ${response.status}`)
            console.log('Raw response data:', response.data)

            const transformedResponse: BillsResponse = {
                items: Array.isArray(response.data.items)
                    ? response.data.items.map(transformBillResponse)
                    : [],
                nextToken: response.data.next_token
            }

            console.log(`[billsApi] Transformed response: ${transformedResponse.items.length} items`)
            return transformedResponse
        } catch (error: any) {
            console.error('Failed to fetch bills:', error.message)
            throw error
        }
    },

    // Removed updateBillWorkflow - no longer using workflow status updates

    getBillVersion: async (session: string, billNumber: string, version: number): Promise<ArrayBuffer> => {
        const encodedSession = encodeURIComponent(session)
        const response = await api.get<ArrayBuffer>(`/bills/${encodedSession}/${billNumber}/versions/${version}`, {
            responseType: 'arraybuffer',
            isWorkspaceSpecific: true
        })
        return response.data as ArrayBuffer
    },

    syncBills: async (session: string): Promise<CreateJobResponse> => {
        try {
            const response = await api.post<CreateJobResponse>('/jobs/sync-bills', {
                session: session.toLowerCase()
            }, {
                isWorkspaceSpecific: true
            })
            return response.data
        } catch (error) {
            console.error('Failed to sync bills:', error)
            throw error
        }
    },

    processBillAction: async ({billNumber, action}: ProcessBillActionRequest): Promise<CreateJobResponse> => {
        // Session code is already set in the workspace context and will be used by the backend
        // isWorkspaceSpecific:true adds /org/{orgId}/ws/{wsId} to the path
        const response = await api.post<CreateJobResponse>(
            `/jobs/process-bills/${billNumber}?bill_action=${action}`,
            undefined,
            { isWorkspaceSpecific: true }
        )
        return response.data
    },

    
    updateAllBillStates: async (orgId?: string, wsId?: string): Promise<any> => {
        if (!orgId || !wsId) {
            // Ensure we have organization and workspace IDs
            throw new Error('Organization and workspace IDs are required');
        }
        
        // This endpoint already includes the org/ws path, so we don't need to use isWorkspaceSpecific
        const response = await api.post<any>(`/org/${orgId}/ws/${wsId}/config/update-states`);
        return response.data;
    },

    bulkPrintReviews: async (session: string, s3Keys: string[]): Promise<{ url: string, processed_files: number, total_keys: number, missing_reviews: string[] }> => {
        try {
            console.log(`Making bulk-print request for ${s3Keys.length} file keys in session ${session}`)
            const response = await api.post<{ url: string, processed_files: number, total_keys: number, missing_reviews: string[] }>(
                '/bills/bulk-print-reviews',
                { session, s3_keys: s3Keys },
                { isWorkspaceSpecific: true }
            )
            console.log('Bulk print API response:', response.data)
            return response.data
        } catch (error) {
            console.error('Failed to generate bulk print PDF:', error)
            throw error
        }
    },

    getAIReview: async (session: string, billNumber: string): Promise<any> => {
        try {
            const encodedSession = encodeURIComponent(session)
            const response = await api.get<any>(`/bills/${encodedSession}/${billNumber}/ai-review`, {
                isWorkspaceSpecific: true
            })
            
            let data = response.data;
            
            // Handle case where data is wrapped in an analysis object with raw_output
            if (data.analysis && data.analysis.raw_output) {
                try {
                    // Extract JSON from raw_output (remove ```json and ``` wrapper if present)
                    const rawOutput = data.analysis.raw_output;
                    const jsonMatch = rawOutput.match(/```json\s*(.*?)\s*```/s);
                    const jsonString = jsonMatch ? jsonMatch[1] : rawOutput;
                    
                    // Parse the extracted JSON and merge with top-level properties
                    const parsedData = JSON.parse(jsonString);
                    data = {
                        ...data,
                        ...parsedData
                    };
                    
                    // Remove the analysis wrapper since we've extracted the data
                    delete data.analysis;
                } catch (parseError) {
                    console.warn('Failed to parse raw_output JSON:', parseError);
                    // Fall back to original data if parsing fails
                }
            }
            // Handle case where report data is nested under analysis (most common case)
            else if (data.analysis && (data.analysis.report_umd || data.analysis.report_higher_ed)) {
                // Extract report data from analysis and merge with top-level properties
                const analysisData = data.analysis;
                data = {
                    ...data,
                    ...analysisData
                };
                
                // Remove the analysis wrapper since we've extracted the data
                delete data.analysis;
            }
            
            return data;
        } catch (error) {
            console.error('Failed to fetch AI review:', error)
            throw error
        }
    }
}


export const useSyncBills = () => {
    return useMutation({
        mutationFn: billsApi.syncBills
    })
}

export const useProcessBillAction = () => {
    return useMutation({
        mutationFn: billsApi.processBillAction
    })
}

export const useGetPresignedUrl = () => {
    return useMutation({
        mutationFn: billsApi.getPresignedUrl
    })
}

export const useBulkPrintReviews = () => {
    return useMutation({
        mutationFn: ({ session, s3Keys }: { session: string, s3Keys: string[] }) =>
            billsApi.bulkPrintReviews(session, s3Keys)
    })
}

export const useGetAIReview = () => {
    return useMutation({
        mutationFn: ({ session, billNumber }: { session: string, billNumber: string }) =>
            billsApi.getAIReview(session, billNumber)
    })
}